import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/* Vendored, not installed — the same reason as the sibling apps: this is a
   white-labelled product and a component pulled from a registry at build time
   is a string we did not write appearing on a surface we promised to control.

   ACTION IS INK. `primary` fills with `action`, which under Graphite resolves to
   near-white on dark and near-black on light — never to the accent. A screen
   may still have at most one primary button; on the review page that button is
   Promote to staging, or Promote to production once staging has merged, never
   both at once.

   Controls are pills. Rectangles are for content, pills are for things you
   press: that split is what keeps a dense operator screen readable at a glance. */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border font-medium transition-colors select-none disabled:pointer-events-none disabled:opacity-45',
  {
    variants: {
      variant: {
        primary: 'border-action bg-action text-action-ink hover:bg-action-hi hover:border-action-hi',
        secondary: 'border-rule-strong bg-raised text-ink hover:bg-surface hover:border-ink-3',
        ghost: 'border-transparent bg-transparent text-ink-2 hover:bg-sunken hover:text-ink',
        danger: 'border-danger bg-danger text-danger-ink hover:opacity-90',
        // Posting a turn the client will never see. It is deliberately NOT the
        // ink pill: the one control on this surface with a different blast
        // radius is the one control that does not look like the others.
        hidden: 'border-hidden bg-hidden text-hidden-ink hover:opacity-90',
        'danger-quiet': 'border-rule-strong bg-raised text-danger hover:bg-danger-soft hover:border-danger',
        link: 'border-transparent bg-transparent text-action underline underline-offset-2 hover:text-action-hi px-0 h-auto',
      },
      size: {
        sm: 'h-7 px-3 text-xs',
        md: 'h-9 px-4 text-sm',
        lg: 'h-10 px-5 text-sm',
        icon: 'h-8 w-8 p-0',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, loading, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : (type ?? 'button')}
        disabled={asChild ? undefined : disabled || loading}
        aria-busy={loading || undefined}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading ? <Spinner /> : null}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

function Spinner() {
  return (
    <span
      aria-hidden
      className="h-3 w-3 shrink-0 animate-ledger-sweep rounded-full border-[1.5px] border-current border-t-transparent opacity-70"
    />
  );
}

export { buttonVariants };
